import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ETIQUETAS_ESTADO_STOCK,
  ETIQUETAS_ESTADO_VENCIMIENTO,
  type Lectura,
  type NivelFrase,
} from '@suminuta/shared';
import { useAuth } from '../../hooks/useAuth';
import {
  useComprasReporte,
  useConciliacion,
  useConteo,
  useExistencias,
  useLotesReporte,
} from '../../hooks/useReportes';
import type { Filtros, NombreReporte } from '../../services/reportesService';
import { formatearPrecio } from '../../utils/facturaCalculos';
import { formatearFechaCon } from '../../utils/dateUtils';

/**
 * Vista de impresión de los reportes (PROMPT_N°054).
 *
 * El "PDF" es la impresión del navegador, con la misma técnica que
 * `DespachoImprimir.tsx`: `@media print` + un botón que llama a
 * `window.print()`. No se
 * agrega ninguna librería de PDF; el usuario elige "Guardar como PDF" en
 * el diálogo del sistema, que es lo que ya hace con el comprobante de
 * despacho.
 *
 * **No se auto-imprime al montar.** Eso fue un bug real en despacho (fix
 * tanda3 H9): el diálogo nativo se abría antes de que el usuario pudiera
 * ver nada. Acá la vista se renderiza y el botón dispara la impresión
 * cuando él decide.
 *
 * Los filtros llegan por query string y se vuelven a consultar con los
 * mismos hooks de la pantalla, así que lo que se imprime es exactamente lo
 * que se estaba viendo.
 */

const TITULOS: Record<NombreReporte, string> = {
  existencias: 'Existencias valorizadas',
  conciliacion: 'Conciliación de movimientos',
  lotes: 'Lotes y vencimientos',
  compras: 'Compras del período',
  conteo: 'Planilla de conteo físico',
};

/**
 * El nivel se marca con símbolo además de color: estas páginas terminan
 * impresas en blanco y negro la mitad de las veces.
 */
const COLOR_NIVEL: Record<NivelFrase, string> = {
  alerta: 'text-red-700',
  atencion: 'text-amber-700',
  ok: 'text-green-700',
  info: 'text-gray-700',
};
const VINETA: Record<NivelFrase, string> = {
  alerta: '!!',
  atencion: '!',
  ok: 'OK',
  info: '·',
};

const fecha = (f: string | null) =>
  f ? formatearFechaCon(f, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const ESTILOS = `
  @media print {
    @page { margin: 10mm; size: A4 landscape; }
    body { background: white !important; }
    .print-root { padding: 0 !important; }
    .no-print { display: none !important; }
    .salto-categoria { break-before: page; page-break-before: always; }
    thead { display: table-header-group; }
    tr { break-inside: avoid; page-break-inside: avoid; }
  }
  .rep { width: 100%; border-collapse: collapse; }
  .rep th, .rep td {
    padding: 4px 6px; font-size: 10px; text-align: left;
    border-bottom: 1px solid #e5e7eb;
  }
  .rep th { background: #f3f4f6; font-weight: 600; }
  .rep td.num, .rep th.num { text-align: right; font-variant-numeric: tabular-nums; }
  .rep tfoot td { font-weight: 700; border-top: 2px solid #374151; }
  .celda-vacia { display: block; height: 14px; border-bottom: 1px dashed #9ca3af; min-width: 60px; }
  .alerta { color: #b91c1c; font-weight: 700; }
`;

export default function ReporteImprimir() {
  const { nombre } = useParams<{ nombre: NombreReporte }>();
  const [params] = useSearchParams();
  const { user } = useAuth();

  const filtros: Filtros = useMemo(() => {
    const f: Filtros = {};
    params.forEach((v, k) => {
      f[k] = v;
    });
    return f;
  }, [params]);

  // Los cinco hooks se declaran siempre (las reglas de hooks no permiten
  // llamarlos condicionalmente) pero sólo el del reporte pedido consulta:
  // sin el `enabled`, abrir una impresión disparaba las cinco queries.
  const es = (n: NombreReporte) => nombre === n;
  const existencias = useExistencias(filtros, es('existencias'));
  const conciliacion = useConciliacion(filtros, es('conciliacion'));
  const lotes = useLotesReporte(filtros, es('lotes'));
  const compras = useComprasReporte(filtros, es('compras'));
  const conteo = useConteo(filtros, es('conteo'));

  const consulta =
    nombre === 'existencias'
      ? existencias
      : nombre === 'conciliacion'
        ? conciliacion
        : nombre === 'lotes'
          ? lotes
          : nombre === 'compras'
            ? compras
            : conteo;

  const lectura = (consulta.data as { lectura?: Lectura } | undefined)?.lectura;

  const hoy = new Date().toLocaleDateString('es-CL', {
    timeZone: 'America/Santiago',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const hora = new Date().toLocaleTimeString('es-CL', {
    timeZone: 'America/Santiago',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const descripcionFiltros = [...params.entries()]
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
    .join('  ·  ');

  if (!nombre || !(nombre in TITULOS)) {
    return <div className="p-8">Reporte desconocido.</div>;
  }

  return (
    <div className="print-root mx-auto p-6 text-gray-900 bg-white">
      <style>{ESTILOS}</style>

      <div className="no-print mb-4 flex justify-end gap-2">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-[#29ABE2] text-white rounded text-sm font-medium"
        >
          🖨️ Imprimir / Guardar PDF
        </button>
      </div>

      <header className="flex items-start justify-between border-b-2 border-[#29ABE2] pb-2 mb-3">
        <div>
          <h1 className="text-xl font-bold text-[#29ABE2]">
            {TITULOS[nombre as NombreReporte]}
          </h1>
          <p className="text-[10px] text-gray-600">QuickTaste · Suminuta</p>
          {descripcionFiltros && (
            <p className="text-[10px] text-gray-500 mt-0.5">
              {descripcionFiltros}
            </p>
          )}
        </div>
        <div className="text-right">
          <div className="text-base font-bold text-[#F7941D]">NexGen</div>
          <p className="text-[10px] text-gray-600">
            {hoy} {hora}
          </p>
        </div>
      </header>

      {/* La lectura va también en el papel: un informe impreso sin ella
          vuelve a ser la tabla muda que el N°056 vino a cerrar. */}
      {lectura && lectura.frases.length > 0 && (
        <section className="mb-4 border border-gray-300 rounded p-2">
          <h2 className="text-xs font-bold uppercase tracking-wide text-gray-700 mb-1">
            Lectura
          </h2>
          <ul className="space-y-0.5">
            {lectura.frases.map((f, i) => (
              <li key={i} className="text-[11px] flex gap-1.5">
                <span className={`font-bold ${COLOR_NIVEL[f.nivel]}`}>
                  {VINETA[f.nivel]}
                </span>
                <span className={COLOR_NIVEL[f.nivel]}>{f.texto}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {consulta.isLoading ? (
        <p className="text-sm text-gray-500">Cargando…</p>
      ) : consulta.error ? (
        <p className="text-sm text-red-600">No se pudo cargar el reporte.</p>
      ) : (
        <>
          {nombre === 'existencias' && existencias.data && (
            <table className="rep">
              <thead>
                <tr>
                  <th>Ingrediente</th>
                  <th>Categoría</th>
                  <th className="num">Stock</th>
                  <th className="num">Mínimo</th>
                  <th>Estado</th>
                  <th className="num">Costo prom.</th>
                  <th className="num">Valor</th>
                  <th className="num">Lotes</th>
                  <th>Vence</th>
                  <th>Proveedor</th>
                </tr>
              </thead>
              <tbody>
                {existencias.data.filas.map((f) => (
                  <tr key={f.id}>
                    <td>{f.nombre}</td>
                    <td>{f.categoria ?? 'Sin categoría'}</td>
                    <td className="num">
                      {f.stock_actual.toLocaleString('es-CL')} {f.unidad}
                    </td>
                    <td className="num">
                      {f.stock_minimo.toLocaleString('es-CL')}
                    </td>
                    <td>{ETIQUETAS_ESTADO_STOCK[f.estado]}</td>
                    <td className="num">
                      {f.costo_promedio > 0
                        ? `$${Math.round(f.costo_promedio).toLocaleString('es-CL')}`
                        : '—'}
                    </td>
                    <td className="num">${formatearPrecio(f.valor)}</td>
                    <td className="num">{f.lotes_activos}</td>
                    <td>{fecha(f.proximo_vencimiento)}</td>
                    <td>{f.proveedor ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6}>
                    TOTAL · {existencias.data.total.ingredientes} ingredientes
                  </td>
                  <td className="num">
                    ${formatearPrecio(existencias.data.total.valor)}
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          )}

          {nombre === 'conciliacion' && conciliacion.data && (
            <table className="rep">
              <thead>
                <tr>
                  <th>Ingrediente</th>
                  <th className="num">Inicial</th>
                  <th className="num">Entradas</th>
                  <th className="num">Reversos</th>
                  <th className="num">Salidas</th>
                  <th className="num">Ajustes</th>
                  <th className="num">Calculado</th>
                  <th className="num">Sistema</th>
                  <th className="num">Dif.</th>
                </tr>
              </thead>
              <tbody>
                {conciliacion.data.filas.map((f) => (
                  <tr key={f.id}>
                    <td>
                      {f.nombre} <span className="text-gray-500">({f.unidad})</span>
                    </td>
                    <td className="num">{f.saldo_inicial.toLocaleString('es-CL')}</td>
                    <td className="num">{f.entradas.toLocaleString('es-CL')}</td>
                    <td className="num">{f.e_reverso.toLocaleString('es-CL')}</td>
                    <td className="num">{f.salidas.toLocaleString('es-CL')}</td>
                    <td className="num">{f.ajustes.toLocaleString('es-CL')}</td>
                    <td className="num">
                      {f.saldo_final_calculado.toLocaleString('es-CL')}
                    </td>
                    <td className="num">
                      {f.saldo_sistema.toLocaleString('es-CL')}
                    </td>
                    <td className={`num ${f.diferencia !== 0 ? 'alerta' : ''}`}>
                      {f.diferencia.toLocaleString('es-CL')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {nombre === 'lotes' && lotes.data && (
            <table className="rep">
              <thead>
                <tr>
                  <th>Ingrediente</th>
                  <th>Lote</th>
                  <th>Origen</th>
                  <th>Ingreso</th>
                  <th>Vence</th>
                  <th>Semáforo</th>
                  <th className="num">Disponible</th>
                  <th className="num">Valor</th>
                </tr>
              </thead>
              <tbody>
                {lotes.data.filas.map((f) => (
                  <tr key={f.id}>
                    <td>{f.ingrediente}</td>
                    <td>{f.numero_lote}</td>
                    <td>{f.origen}</td>
                    <td>{fecha(f.fecha_ingreso)}</td>
                    <td>{fecha(f.fecha_vencimiento)}</td>
                    <td className={f.semaforo === 'vencido' ? 'alerta' : ''}>
                      {ETIQUETAS_ESTADO_VENCIMIENTO[f.semaforo]}
                    </td>
                    <td className="num">
                      {f.disponible.toLocaleString('es-CL')} {f.unidad}
                    </td>
                    <td className="num">
                      ${formatearPrecio(f.valor_disponible)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6}>TOTAL · {lotes.data.total.lotes} lotes</td>
                  <td />
                  <td className="num">
                    ${formatearPrecio(lotes.data.total.valor_disponible)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}

          {nombre === 'compras' && compras.data && (
            <>
              <h2 className="text-sm font-bold mt-2 mb-1">Por proveedor</h2>
              <table className="rep">
                <thead>
                  <tr>
                    <th>Proveedor</th>
                    <th className="num">Facturas</th>
                    <th className="num">Neto</th>
                    <th className="num">IVA</th>
                    <th className="num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {compras.data.porProveedor.map((p) => (
                    <tr key={p.proveedor}>
                      <td>{p.proveedor}</td>
                      <td className="num">{p.facturas}</td>
                      <td className="num">${formatearPrecio(p.neto)}</td>
                      <td className="num">${formatearPrecio(p.iva)}</td>
                      <td className="num">${formatearPrecio(p.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>TOTAL</td>
                    <td className="num">{compras.data.total.facturas}</td>
                    <td className="num">
                      ${formatearPrecio(compras.data.total.neto)}
                    </td>
                    <td className="num">
                      ${formatearPrecio(compras.data.total.iva)}
                    </td>
                    <td className="num">
                      ${formatearPrecio(compras.data.total.total)}
                    </td>
                  </tr>
                </tfoot>
              </table>

              {compras.data.recepcionesSinFactura.length > 0 && (
                <>
                  <h2 className="text-sm font-bold mt-4 mb-1">
                    Recepciones sin factura{' '}
                    <span className="font-normal text-[10px] text-gray-600">
                      (no suman al total)
                    </span>
                  </h2>
                  <table className="rep">
                    <thead>
                      <tr>
                        <th>Orden</th>
                        <th>Recepción</th>
                        <th>Proveedor</th>
                        <th className="num">Lotes</th>
                        <th className="num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {compras.data.recepcionesSinFactura.map((r) => (
                        <tr key={r.id}>
                          <td>{r.numero_orden}</td>
                          <td>{fecha(r.fecha)}</td>
                          <td>{r.proveedor ?? '—'}</td>
                          <td className="num">{r.lineas}</td>
                          <td className="num">${formatearPrecio(r.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}

          {nombre === 'conteo' && conteo.data && (
            <>
              {conteo.data.grupos.map((g, i) => (
                <div key={g.categoria} className={i > 0 ? 'salto-categoria' : ''}>
                  <h2 className="text-sm font-bold mt-3 mb-1">
                    {g.categoria}{' '}
                    <span className="font-normal text-[10px] text-gray-600">
                      ({g.filas.length} ingredientes)
                    </span>
                  </h2>
                  <table className="rep">
                    <thead>
                      <tr>
                        <th style={{ width: '32%' }}>Ingrediente</th>
                        <th style={{ width: '16%' }}>Marca</th>
                        <th style={{ width: '8%' }}>Unidad</th>
                        {conteo.data!.mostrar_sistema && (
                          <th className="num" style={{ width: '10%' }}>
                            Sistema
                          </th>
                        )}
                        <th style={{ width: '12%' }}>Conteo</th>
                        {conteo.data!.mostrar_sistema && (
                          <th style={{ width: '10%' }}>Diferencia</th>
                        )}
                        <th>Observación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.filas.map((f) => (
                        <tr key={f.id}>
                          <td>{f.nombre}</td>
                          <td>{f.marca ?? ''}</td>
                          <td>{f.unidad}</td>
                          {conteo.data!.mostrar_sistema && (
                            <td className="num">
                              {(f.stock_sistema ?? 0).toLocaleString('es-CL')}
                            </td>
                          )}
                          <td>
                            <span className="celda-vacia" />
                          </td>
                          {conteo.data!.mostrar_sistema && (
                            <td>
                              <span className="celda-vacia" />
                            </td>
                          )}
                          <td>
                            <span className="celda-vacia" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              <div className="mt-8 flex gap-12 text-[11px]">
                <div>
                  Contado por: <span className="inline-block w-56 border-b border-gray-500" />
                </div>
                <div>
                  Fecha: <span className="inline-block w-32 border-b border-gray-500" />
                </div>
                <div>
                  Firma: <span className="inline-block w-48 border-b border-gray-500" />
                </div>
              </div>
            </>
          )}
        </>
      )}

      <footer className="mt-6 pt-2 border-t border-gray-300 text-[10px] text-gray-600 flex justify-between">
        <span>
          Generado por {user?.nombre ?? 'Sistema'} el {hoy} {hora}
        </span>
        <span>Suminuta · {TITULOS[nombre as NombreReporte]}</span>
      </footer>
    </div>
  );
}
